import { APP_API_URL } from './api-config.service'

export const CONNECTION_CHECK_INTERVAL_MS = 30000
export const CONNECTION_RETRY_INTERVAL_MS = 10000
export const CONNECTION_REQUEST_TIMEOUT_MS = 8000

export interface ConnectionState {
    readonly server: 'checking' | 'online' | 'offline'
    readonly database: 'checking' | 'online' | 'offline' | 'unknown'
    readonly checkedAt: number | null
    readonly reason?: 'browser-offline' | 'request-failed' | 'invalid-response'
}

export const INITIAL_CONNECTION_STATE: ConnectionState = {
    server: 'checking', database: 'checking', checkedAt: null,
}

interface ConnectionHealth {
    server: 'online'
    database: 'online' | 'offline'
    checkedAt: string
}

type ConnectionHealthLoader = (signal: AbortSignal) => Promise<ConnectionHealth>
type ConnectionListener = (state: ConnectionState) => void

class InvalidConnectionResponseError extends Error {}

const isConnectionHealth = (value: unknown): value is ConnectionHealth => {
    if (!value || typeof value !== 'object') return false
    const data = value as Partial<ConnectionHealth>
    return data.server === 'online' && ['online', 'offline'].includes(data.database || '')
        && typeof data.checkedAt === 'string'
        && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/.test(data.checkedAt)
        && Number.isFinite(Date.parse(data.checkedAt))
}

// The public probe deliberately bypasses API retries, auth refresh and error
// reporting: a failed health check must not prolong itself or create more traffic.
export const fetchConnectionHealth: ConnectionHealthLoader = async signal => {
    const response = await fetch(`${APP_API_URL}/systems/connectionStatus`, {
        method: 'GET', signal, credentials: 'omit', cache: 'no-store',
        headers: {Accept: 'application/json'},
    })
    if (!response.ok) throw new Error('Connection probe failed')
    let payload: unknown
    try {payload = await response.json()}
    catch {throw new InvalidConnectionResponseError('Invalid connection response')}
    const body = payload as {status?: unknown; data?: unknown} | null
    if (body?.status !== 200 || !isConnectionHealth(body.data)) {
        throw new InvalidConnectionResponseError('Invalid connection response')
    }
    return body.data
}

export class ConnectionMonitorService {
    private state: ConnectionState = INITIAL_CONNECTION_STATE
    private readonly listeners = new Set<ConnectionListener>()
    private running = false
    private generation = 0
    private timer: ReturnType<typeof setTimeout> | null = null
    private pending: {controller: AbortController; promise: Promise<void>} | null = null

    constructor(private readonly loadHealth: ConnectionHealthLoader = fetchConnectionHealth) {}

    getSnapshot = () => this.state

    subscribe = (listener: ConnectionListener) => {
        this.listeners.add(listener)
        listener(this.state)
        return () => {this.listeners.delete(listener)}
    }

    start = (): Promise<void> => {
        if (this.running) return this.pending?.promise || Promise.resolve()
        this.running = true
        this.publish(INITIAL_CONNECTION_STATE)
        window.addEventListener('online', this.handleOnline)
        window.addEventListener('offline', this.handleOffline)
        window.addEventListener('focus', this.handleFocus)
        document.addEventListener('visibilitychange', this.handleVisibility)
        return this.checkNow()
    }

    stop = () => {
        this.running = false
        this.clearTimer()
        this.cancelPending()
        window.removeEventListener('online', this.handleOnline)
        window.removeEventListener('offline', this.handleOffline)
        window.removeEventListener('focus', this.handleFocus)
        document.removeEventListener('visibilitychange', this.handleVisibility)
    }

    checkNow = (): Promise<void> => {
        if (!this.running || document.visibilityState === 'hidden') return Promise.resolve()
        if (navigator.onLine === false) {
            this.handleOffline()
            return Promise.resolve()
        }
        if (this.pending) return this.pending.promise
        this.clearTimer()
        const generation = this.generation
        const controller = new AbortController()
        let timeout: ReturnType<typeof setTimeout>
        let handleAbort: () => void
        const request = new Promise<ConnectionHealth>((resolve, reject) => {
            handleAbort = () => reject(new Error('Connection probe cancelled'))
            controller.signal.addEventListener('abort', handleAbort, {once: true})
            timeout = setTimeout(() => controller.abort(), CONNECTION_REQUEST_TIMEOUT_MS)
            Promise.resolve().then(() => {
                if (controller.signal.aborted) throw new Error('Connection probe cancelled')
                return this.loadHealth(controller.signal)
            }).then(resolve, reject)
        })
        const promise = request.then(health => {
            if (!this.running || generation !== this.generation) return
            if (!isConnectionHealth(health)) throw new InvalidConnectionResponseError('Invalid connection response')
            this.publish({server: 'online', database: health.database, checkedAt: Date.parse(health.checkedAt)})
        }).catch(error => {
            if (!this.running || generation !== this.generation) return
            this.publish({server: 'offline', database: 'unknown', checkedAt: Date.now(),
                reason: error instanceof InvalidConnectionResponseError ? 'invalid-response' : 'request-failed'})
        }).finally(() => {
            clearTimeout(timeout)
            controller.signal.removeEventListener('abort', handleAbort)
            if (this.pending?.controller !== controller) return
            this.pending = null
            this.schedule()
        })
        this.pending = {controller, promise}
        return promise
    }

    private publish = (state: ConnectionState) => {
        this.state = state
        this.listeners.forEach(listener => listener(state))
    }

    private clearTimer = () => {
        if (this.timer !== null) clearTimeout(this.timer)
        this.timer = null
    }

    private cancelPending = () => {
        this.generation += 1
        const request = this.pending
        this.pending = null
        request?.controller.abort()
    }

    private schedule = () => {
        if (!this.running || document.visibilityState === 'hidden' || navigator.onLine === false) return
        const delay = this.state.server === 'online' && this.state.database === 'online'
            ? CONNECTION_CHECK_INTERVAL_MS : CONNECTION_RETRY_INTERVAL_MS
        this.timer = setTimeout(() => {this.timer = null; void this.checkNow()}, delay)
    }

    private handleOnline = () => {void this.checkNow()}
    private handleFocus = () => {void this.checkNow()}
    private handleOffline = () => {
        if (!this.running) return
        this.clearTimer()
        this.cancelPending()
        this.publish({server: 'offline', database: 'unknown', checkedAt: Date.now(), reason: 'browser-offline'})
    }
    private handleVisibility = () => {
        if (document.visibilityState !== 'hidden') {void this.checkNow(); return}
        this.clearTimer()
        this.cancelPending()
    }
}

const connectionMonitorService = new ConnectionMonitorService()
export default connectionMonitorService
